import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines, SectionLabel } from "@/components/Reveal";
import ROICalculator from "@/components/ROICalculator";
import { PRODUCTS, PRODUCT_CATEGORIES } from "@/data/content";

export default function Products() {
  const [filter, setFilter] = useState("All");
  const navigate = useNavigate();
  const visible = filter === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === filter);

  return (
    <main data-testid="products-page" className="pt-32">
      <section className="grid-bg border-b border-zinc-900/10 pb-20">
        <div className="mx-auto max-w-7xl px-6">
          <SectionLabel>Product Suite</SectionLabel>
          <MaskedLines
            className="mt-6"
            lines={["Ten ways to get", <span key="a" className="text-cyan-600">your time back.</span>]}
            lineClassName="font-heading text-5xl font-bold tracking-tighter leading-[1.05] text-zinc-900 sm:text-6xl lg:text-7xl"
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }} className="mt-8 max-w-2xl text-lg leading-relaxed text-zinc-600">
            Every product below solves a real headache — buried paperwork, slow quotes, dropped follow-ups. Start with the one that hurts most and see results in the first week. They all work together, so each one you add makes the others better.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <div data-testid="product-filter-tabs" className="flex flex-wrap gap-2">
          {PRODUCT_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-tab-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                filter === c ? "border-cyan-500 bg-cyan-600 text-white" : "border-zinc-900/20 text-zinc-600 hover:border-zinc-900/40 hover:text-zinc-900"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <div data-testid={`product-card-${p.id}`} className="card-hover group flex h-full flex-col border border-zinc-900/10 bg-[#FFFFFF] p-10">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-cyan-600">/{p.num}</span>
                    <span className="border border-zinc-900/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.category}</span>
                  </div>
                  <h3 className="mt-8 font-heading text-2xl font-semibold text-zinc-900">{p.name}</h3>
                  <p className="mt-2 font-mono text-xs uppercase tracking-[0.15em] text-cyan-600">{p.tagline}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-zinc-600">{p.description}</p>
                  <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-zinc-900/10 pt-6">
                    <div className="flex flex-wrap gap-2">
                      {p.verticals.map((v) => (
                        <span key={v} className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">{v}</span>
                      ))}
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-blue-600">{p.engine}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </section>

      <ROICalculator />

      <section className="border-t border-zinc-900/10 bg-[#F0F2F5] py-24">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6">
          <Reveal>
            <h2 className="font-heading text-3xl font-semibold text-zinc-900 sm:text-4xl">Not sure where to start?</h2>
            <p className="mt-3 text-zinc-600">Tell us your biggest time-drain. In 30 free minutes, we'll show you exactly what AI can take off your plate — no tech talk, no obligation.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <button data-testid="products-cta-demo-btn" onClick={() => navigate("/contact")} className="btn-primary">
              Book Your Free Demo <ArrowUpRight size={16} />
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
